import { useState } from 'react';
import useDigitalDownloads from '@/hooks/useDigitalDownloads';
import type { ZifekUser } from '@/hooks/useAuth';

interface Props {
  user: ZifekUser;
}

function fmtDate(d: string | null): string {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return d;
  }
}

/** Icône + libellé de type de fichier à partir du nom/extension. */
function fileKind(productName: string, fileName: string | null): { icon: string; label: string } {
  const src = (fileName || productName || '').toLowerCase();
  if (/\.(mp4|mov|webm|avi|mkv|m4v)$/.test(src)) return { icon: 'ri-video-line', label: 'Vidéo' };
  if (/\.(mp3|wav|ogg|m4a|aac)$/.test(src)) return { icon: 'ri-music-2-line', label: 'Audio' };
  if (/\.(jpg|jpeg|png|webp|gif|svg)$/.test(src)) return { icon: 'ri-image-line', label: 'Image' };
  if (/\.pdf$/.test(src)) return { icon: 'ri-file-pdf-2-line', label: 'PDF' };
  if (/\.(zip|rar|7z)$/.test(src)) return { icon: 'ri-file-zip-line', label: 'Archive' };
  if (/\.(doc|docx|odt)$/.test(src)) return { icon: 'ri-file-word-2-line', label: 'Document' };
  return { icon: 'ri-file-line', label: 'Fichier' };
}

/**
 * « Mes téléchargements » : les produits DIGITAUX achetés par le client
 * connecté, avec un bouton de téléchargement direct du fichier.
 */
export default function DownloadsTab({ user }: Props) {
  const { downloads, loading, error, reload } = useDigitalDownloads(user?.id);
  const [search, setSearch] = useState('');

  const filtered = downloads.filter((d) =>
    d.name.toLowerCase().includes(search.toLowerCase()),
  );
  const readyCount = downloads.filter((d) => d.url).length;

  return (
    <div className="max-w-4xl xl:max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
            Mes téléchargements
          </h2>
          <p className="text-xs text-foreground-500 mt-1">
            {downloads.length} fichier{downloads.length !== 1 ? 's' : ''} numérique{downloads.length !== 1 ? 's' : ''} acheté{downloads.length !== 1 ? 's' : ''}
            {readyCount > 0 ? ` · ${readyCount} prêt${readyCount !== 1 ? 's' : ''} à télécharger` : ''}
          </p>
        </div>
      </div>

      {/* Erreur */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 bg-red-50 text-red-700 text-xs rounded-lg mb-4">
          <i className="ri-error-warning-line"></i>
          <span className="flex-1">{error}</span>
          <button onClick={reload} className="cursor-pointer font-semibold whitespace-nowrap hover:text-red-900">
            Réessayer
          </button>
        </div>
      )}

      {/* Bandeau d'information */}
      {!loading && !error && downloads.length > 0 && (
        <div className="flex items-start gap-2.5 px-4 py-3 bg-secondary-50 text-secondary-700 rounded-lg mb-5 text-xs leading-relaxed">
          <i className="ri-information-line text-base flex-shrink-0 mt-0.5"></i>
          <span>
            Retrouvez ici tous les produits numériques (livres, vidéos, PDF…) que vous avez achetés.
            Vos fichiers restent accessibles à tout moment depuis cet espace.
          </span>
        </div>
      )}

      {/* Recherche */}
      {!loading && !error && downloads.length > 0 && (
        <div className="relative mb-5 max-w-md">
          <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            placeholder="Rechercher un fichier..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-background-50 border border-background-200/40 rounded-lg text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-foreground-300 transition-colors"
          />
        </div>
      )}

      {/* Contenu */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-xl text-foreground-400"></i>
        </div>
      ) : error ? null : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/30 rounded-xl">
          <div className="w-14 h-14 rounded-2xl bg-foreground-100 flex items-center justify-center mb-4">
            <i className="ri-download-cloud-2-line text-xl text-foreground-500"></i>
          </div>
          <h3 className="text-foreground-800 font-semibold text-sm mb-1">
            {search ? 'Aucun résultat' : 'Aucun téléchargement'}
          </h3>
          <p className="text-foreground-400 text-xs text-center max-w-xs">
            {search
              ? 'Aucun fichier ne correspond à votre recherche.'
              : 'Vos produits numériques achetés (livres, vidéos, PDF…) apparaîtront ici une fois votre commande passée.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((d) => {
            const kind = fileKind(d.name, d.fileName);
            return (
              <div
                key={`${d.productId}-${d.orderId ?? 'acces'}`}
                className="bg-background-50 border border-background-200/40 rounded-xl p-4 flex items-start gap-3 hover:border-foreground-200/60 transition-colors"
              >
                <div className="w-11 h-11 rounded-lg bg-foreground-100 flex items-center justify-center flex-shrink-0">
                  <i className={`${kind.icon} text-lg text-foreground-600`}></i>
                </div>

                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground-900 truncate">{d.name}</p>
                  <p className="text-[11px] text-foreground-400 mt-0.5">
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-file-line"></i>{kind.label}
                    </span>
                    <span className="mx-1.5">·</span>
                    {d.orderId ? `Commande #${d.orderId}` : (d.source === 'manual' ? 'Accès offert' : 'Accès activé')}
                    <span className="mx-1.5">·</span>
                    {fmtDate(d.date)}
                  </p>
                  {d.note && (
                    <p className="text-[11px] text-foreground-500 mt-1.5 leading-relaxed">{d.note}</p>
                  )}

                  <div className="mt-3">
                    {d.url ? (
                      <a
                        href={d.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold bg-foreground-900 text-background-50 hover:bg-foreground-800 transition-colors no-underline whitespace-nowrap"
                      >
                        <i className="ri-download-2-line"></i>
                        Télécharger
                      </a>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium bg-background-100 text-foreground-500 whitespace-nowrap">
                        <i className="ri-time-line"></i>
                        Lien en préparation
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}